import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useMenu } from '../../hooks/useMenu';
import useAipFlow from '../../hooks/useAipFlow';
import { selectApplication } from '../../store/selectors/applicationSelectors';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import {
  ACCOUNT_TYPE,
  ACCOUNT_TYPE_GTM,
  BASIC_ACCOUNT_QUERY_DATA,
  REGISTRATION_FLOW_TYPE,
} from '../../constants/sessionStorage';
import useStepPost from '../../hooks/useStepPost';
import { zaiusSubscribe } from '../../zaiusSubscribe';
import {
  APPLICATION_TYPE_STRING,
  BASIC_ACCOUNT_UPGRADE_TYPES,
  CorporateAccountType,
  REGISTRATION_FLOW_TYPES,
} from '../../util/constants';
import Paragraph from '../../components/Paragraph/Paragraph';
import { HeadingSubtitle } from '../../components/Heading';
import OnBoardingContainer from '../../components/OnBoarding/OnBoardingContainer';
import { ParagraphRegular } from '../../components/Paragraph';
import { ListItem, ListParagraph } from '../../components/SelectionList';
import OnBoardingCta from '../../components/OnBoarding/OnBoardingCta';
import Lock from '../../components/Icons/Lock';
import themeColors from '../../assets/styles/themeColors';
import useApplicationUid from '../../hooks/useApplicationUid';
import {
  createApplicationStep,
  getApplication,
} from '../../store/actions/application/applicationActions';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import SaveAndCloseModal from '../../components/Modals/SaveAndCloseModal/SaveAndCloseModal';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { ACCOUNT_STEP_COMPLETE } from '../../util/gtmEvents';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';

const List = styled.div`
  padding-top: 32px;
`;
const SecureAccount = () => {
  const { t } = useTranslation();
  const { activeMenu, activeItem } = useMenu();
  const { isAipFlow } = useAipFlow();
  const { ApplicationUid } = useApplicationUid();
  const dispatch = useDispatch();
  const basicAccountQueryData = retrieveFromSessionStorage(
    BASIC_ACCOUNT_QUERY_DATA,
  );
  const Application = useSelector(selectApplication);
  const { ApplicationType, AccountUpgradeType } = Application;
  const AccountType = retrieveFromSessionStorage(ACCOUNT_TYPE);
  const isUpgrade =
    AccountUpgradeType === BASIC_ACCOUNT_UPGRADE_TYPES.IN_PROCESS;
  const { onSuccess } = useStepPost({ nextPath: activeItem.nextPath });
  const registrationFlowType = retrieveFromSessionStorage(
    REGISTRATION_FLOW_TYPE,
  );
  const settings = useSelector(selectSettings);
  const { BrokerName } = settings;
  const [eventType, setEventType] = useState('NoForm');
  const [saveAndCloseModalOpen, setSaveAndCloseModalOpen] = useState(false);
  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  const handleGtmEvent = () => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: isAipFlow
        ? 'MetalStream Secure'
        : 'Secure Account Introduction',
      accountType: accountGtmType,
    });
  };

  useEffect(() => {
    if (registrationFlowType !== REGISTRATION_FLOW_TYPES.AIP) {
      zaiusSubscribe();
    }
  }, [registrationFlowType]);

  useEffect(() => {
    const sameApplication = Application?.ApplicationUid === ApplicationUid;
    if (activeItem?.path && sameApplication) {
      dispatch(
        createApplicationStep({
          ApplicationUid,
          data: {
            Step: activeItem?.path,
          },
        }),
      );
    }
  }, [dispatch, ApplicationUid, activeItem, Application.ApplicationUid]);

  useEffect(() => {
    if (isUpgrade && basicAccountQueryData?.accountUid) {
      dispatch(getApplication({ data: { ApplicationUid } }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const returnInformations = () => {
    switch (APPLICATION_TYPE_STRING[ApplicationType]) {
      case APPLICATION_TYPE_STRING[1]:
      case APPLICATION_TYPE_STRING[3]: {
        const informations = [t('secure.basics'), t('secure.ifInUs')];
        return informations;
      }
      case APPLICATION_TYPE_STRING[2]: {
        if (AccountType === CorporateAccountType[4]) {
          const informations = [
            t('secure.trust.basics'),
            t('secure.trust.ifInUs'),
            t('secure.trust.einInfo'),
          ];
          return informations;
        }

        const informations = [
          t('secure.business.basics'),
          t('secure.business.ifInUs'),
          t('secure.business.einInfo'),
        ];
        return informations;
      }
      case APPLICATION_TYPE_STRING[4]: {
        const informations = [
          t('secure.joint.basics'),
          t('secure.joint.ifInUs'),
        ];
        return informations;
      }
      case APPLICATION_TYPE_STRING[5]: {
        const informations = [
          t('secure.utma.basics'),
          t('secure.utma.ifInUs'),
          t('secure.utma.einInfo'),
        ];
        return informations;
      }
      default: {
        const informations = [t('secure.basics'), t('secure.ifInUs')];
        return informations;
      }
    }
  };

  const returnNote = () => {
    const { ApplicationType } = Application;

    switch (APPLICATION_TYPE_STRING[ApplicationType]) {
      case APPLICATION_TYPE_STRING[1]:
      case APPLICATION_TYPE_STRING[3]:
        return;
      case APPLICATION_TYPE_STRING[2]: {
        if (AccountType === CorporateAccountType[4]) {
          return (
            <Paragraph color={themeColors.colorTextXLLight} mb={12}>
              {' '}
              {t('secure.trust.note')}
            </Paragraph>
          );
        }

        return (
          <Paragraph color={themeColors.colorTextXLLight} mb={12}>
            {' '}
            {t('secure.business.note')}
          </Paragraph>
        );
      }
      case APPLICATION_TYPE_STRING[4]:
        return (
          <Paragraph color={themeColors.colorTextXLLight} mb={12}>
            {' '}
            {t('secure.joint.note')}
          </Paragraph>
        );
      case APPLICATION_TYPE_STRING[5]:
        return (
          <Paragraph color={themeColors.colorTextXLLight} mb={12}>
            {' '}
            {t('secure.utma.note')}
          </Paragraph>
        );
      default:
        return (
          <Paragraph color={themeColors.colorTextXLLight} mb={12}>
            {' '}
            {t('secure.additionalBusinessInfo')}
          </Paragraph>
        );
    }
  };

  return (
    <OnBoardingContainer
      activeMenu={activeMenu}
      activeItem={activeItem}
      previousPath={activeItem?.previousPath}
      setEventType={setEventType}
      eventType={eventType}
      setSaveAndCloseModalOpen={setSaveAndCloseModalOpen}
    >
      <Helmet>
        <title>
          {t('titleTagSecureAccount', {
            brokerName: BrokerName,
          })}
        </title>
      </Helmet>
      <HeadingSubtitle color={themeColors.colorTextSecondary}>
        {isAipFlow
          ? t('metalstream.secureYourAccount')
          : t('secure.secureYourAccount')}
      </HeadingSubtitle>

      <Paragraph color={themeColors.colorTextXLLight} mb={20}>
        {t('secure.storePreciousMetals')}
      </Paragraph>
      <List>
        <ParagraphRegular weight={600} mb={12}>
          {' '}
          {t('secure.whatNeed')}
        </ParagraphRegular>
        {returnNote()}
        {returnInformations().map((item) => (
          <ListItem key={uuid()}>
            <ListParagraph color={themeColors.colorTextXLLight} mb={12}>
              {item}
            </ListParagraph>
          </ListItem>
        ))}
      </List>
      <OnBoardingCta
        icon={<Lock color={themeColors.colorWhite} />}
        title={t('secure.secureMyAccount')}
        onClick={() => {
          onSuccess();
          handleGtmEvent();
        }}
      />
      {saveAndCloseModalOpen && (
        <SaveAndCloseModal
          isOpen={saveAndCloseModalOpen}
          onClose={() => setSaveAndCloseModalOpen(false)}
          onSuccess={onSuccess}
        />
      )}
    </OnBoardingContainer>
  );
};

export default SecureAccount;
